import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  X, 
  Globe, 
  ExternalLink, 
  Download, 
  Upload, 
  CheckCircle, 
  AlertCircle,
  Settings,
  Copy,
  Eye,
  Code,
  Zap,
  Cloud,
  Server
} from 'lucide-react';
import toast from 'react-hot-toast';

const PublishModal = ({ isOpen, onClose, websiteData }) => {
  const [selectedPlatform, setSelectedPlatform] = useState('netlify');
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishStep, setPublishStep] = useState('platform');
  const [customDomain, setCustomDomain] = useState('');
  const [deploymentUrl, setDeploymentUrl] = useState('');

  const platforms = [
    {
      id: 'netlify',
      name: 'Netlify',
      description: 'Deploy instantly with automatic builds',
      icon: Cloud,
      color: 'from-green-500 to-green-600',
      features: ['Automatic builds', 'Global CDN', 'Custom domains', 'SSL certificates']
    },
    {
      id: 'vercel',
      name: 'Vercel',
      description: 'Deploy with zero configuration',
      icon: Zap,
      color: 'from-black to-gray-800',
      features: ['Zero config', 'Edge functions', 'Analytics', 'Preview deployments']
    },
    {
      id: 'github-pages',
      name: 'GitHub Pages',
      description: 'Host directly from your GitHub repository',
      icon: Code,
      color: 'from-gray-700 to-gray-900',
      features: ['Free hosting', 'Git integration', 'Custom domains', 'HTTPS']
    },
    {
      id: 'custom',
      name: 'Custom Server',
      description: 'Deploy to your own server or hosting provider',
      icon: Server,
      color: 'from-blue-500 to-blue-600',
      features: ['Full control', 'Custom setup', 'Any hosting provider', 'Manual deployment']
    }
  ];

  const handlePublish = async () => {
    setIsPublishing(true);
    setPublishStep('publishing');

    // Simulate publishing process
    setTimeout(() => {
      const mockUrl = `https://${selectedPlatform}-${Date.now()}.netlify.app`;
      setDeploymentUrl(mockUrl);
      setPublishStep('success');
      setIsPublishing(false);
      toast.success('Website published successfully!');
    }, 3000);
  };

  const handleDownload = () => {
    // Create a blob with the website HTML
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${websiteData?.title || 'My Website'}</title>
    <style>
        /* Add your CSS here */
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
    </style>
</head>
<body>
    <h1>${websiteData?.title || 'My Website'}</h1>
    <p>This is your published website!</p>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'index.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Website downloaded successfully!');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('URL copied to clipboard!');
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <Globe className="w-6 h-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Publish Your Website
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {publishStep === 'platform' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Choose Your Publishing Platform
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Select where you'd like to deploy your website. Each platform offers different features and benefits.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {platforms.map((platform) => (
                  <motion.button
                    key={platform.id}
                    onClick={() => setSelectedPlatform(platform.id)}
                    className={`p-6 border-2 rounded-xl text-left transition-all ${
                      selectedPlatform === platform.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center space-x-4 mb-4">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${platform.color} flex items-center justify-center`}>
                        <platform.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{platform.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{platform.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {platform.features.map((feature, index) => (
                        <li key={index} className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.button>
                ))}
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleDownload}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download HTML</span>
                </button>
                <button
                  onClick={() => setPublishStep('settings')}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>Continue</span>
                </button>
              </div>
            </motion.div>
          )}

          {publishStep === 'settings' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Publishing Settings
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Configure your deployment settings for {platforms.find(p => p.id === selectedPlatform)?.name}.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Custom Domain (Optional)
                  </label>
                                     <input
                     type="text"
                     value={customDomain}
                     onChange={(e) => setCustomDomain(e.target.value)}
                     placeholder="yourdomain.com"
                     className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white bg-white dark:bg-gray-800"
                   />
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Deployment Preview</h4>
                  <div className="bg-white dark:bg-gray-800 rounded border p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {customDomain || `${selectedPlatform}-${Date.now()}.netlify.app`}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setPublishStep('platform')}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handlePublish}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>Publish Now</span>
                </button>
              </div>
            </motion.div>
          )}

          {publishStep === 'publishing' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Publishing Your Website
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we deploy your website to {platforms.find(p => p.id === selectedPlatform)?.name}...
              </p>
            </motion.div>
          )}

          {publishStep === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Website Published Successfully!
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Your website is now live and accessible to the world.
              </p>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Globe className="w-5 h-5 text-primary-600" />
                    <span className="font-medium text-gray-900 dark:text-white">Live URL:</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(deploymentUrl)}
                    className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <Copy className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded border">
                  <a
                    href={deploymentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 break-all"
                  >
                    {deploymentUrl}
                  </a>
                </div>
              </div>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => window.open(deploymentUrl, '_blank')}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                >
                  <Eye className="w-4 h-4" />
                  <span>View Website</span>
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PublishModal;
